import { useEffect, useMemo, useState } from "react";
import { Modal } from "./Modal";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import type { Employee } from "../lib/types";

/**
 * A worker's agreed rate card: an exact $/hr per day type, paid AS-IS on their
 * timesheets. A blank box means that day type falls back to the award-style
 * calculation (base rate x the pay multiplier -- pay_casual for casuals, with
 * the 25% loading embedded). Fixed-rate casuals and ABN contractors get their
 * agreed card here; SCHADS workers leave it blank.
 *
 * Saving reconciles the six drafts against what's stored (create / update /
 * delete per day type) -- same pattern as the provider Negotiated rates card.
 */

interface Rate { id: string; employee_id: string; day_type: string; hourly_rate: string; }

const DAY_TYPES: Array<[string, string]> = [
    ["weekday_day", "Weekday AM (day)"],
    ["weekday_evening", "Weekday PM (evening)"],
    ["weekday_night", "Weekday night"],
    ["saturday", "Saturday — any time"],
    ["sunday", "Sunday — any time"],
    ["public_holiday", "Public holiday — any time"],
];
const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : NaN; };

export function RateCardEditor({ employee, onClose }: {
    employee: Employee;
    onClose: () => void;
}) {
    const rates = useResource<Rate[]>(`/employee-day-rates?employee_id=${employee.id}`);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    const existingByDay = useMemo(
        () => new Map((rates.data ?? []).map((r) => [r.day_type, r])), [rates.data]);

    useEffect(() => {
        const seed: Record<string, string> = {};
        for (const r of rates.data ?? []) seed[r.day_type] = String(num(r.hourly_rate));
        setDraft(seed);
    }, [rates.data]);

    const save = async () => {
        setBusy(true); setMsg(null);
        try {
            for (const [dt] of DAY_TYPES) {
                const raw = (draft[dt] ?? "").trim();
                const val = raw === "" ? null : num(raw);
                const row = existingByDay.get(dt);
                if (val == null || Number.isNaN(val)) {
                    if (row) await api.delete(`/employee-day-rates/${row.id}`);
                } else if (row) {
                    if (num(row.hourly_rate) !== val) {
                        await api.put(`/employee-day-rates/${row.id}`,
                            { employee_id: employee.id, day_type: dt, hourly_rate: val });
                    }
                } else {
                    await api.post("/employee-day-rates",
                        { employee_id: employee.id, day_type: dt, hourly_rate: val });
                }
            }
            await rates.reload();
            setMsg("Saved. Timesheets pick these up automatically.");
        } catch (err) {
            setMsg(err instanceof Error ? err.message : "Could not save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={`Rate card — ${employee.full_name}`}
            subtitle="Agreed $/hr per period, paid as-is. Blank = award calculation (base rate × loading)."
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : "Save rate card"}
                    </button>
                    <button className="btn ghost" disabled={busy} onClick={onClose}>Close</button>
                </>
            }
        >
            <div className="form-grid">
                {DAY_TYPES.map(([key, label]) => (
                    <div className="field half" key={key}>
                        <label>{label}</label>
                        <input type="number" min={0} step="0.01"
                            value={draft[key] ?? ""}
                            placeholder={`award (base $${num(employee.default_hourly_rate) || 0}/hr × loading)`}
                            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
                    </div>
                ))}
            </div>
            {msg && <p className="help" style={{ marginTop: "0.5rem" }}>{msg}</p>}
        </Modal>
    );
}
